import { useMemo, useState } from "react";
import NewNavbar from "../components/NewNavbar";
import ProductCard from "../components/ProductCard";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../components/Footer.js";
import {
  useGetProductsAllProductsQuery,
  usePublicProductsAllListQuery,
} from "../store/api.ts";
import toast from "react-hot-toast";
import SEO from "../components/SEO.js";
import SaveToBoardModal from "../components/SaveToBoardModal.js";
import { getVisibleCardMatchPercentage } from "../utils.js";

const ALL_PRODUCTS_PAGE = 1;
const ALL_PRODUCTS_PAGE_SIZE = 1000;

const normalizeProductForCard = (product) => {
  const score =
    product?.product_score !== null && product?.product_score !== undefined
      ? Math.round(Number(product.product_score))
      : null;

  return {
    ...product,
    id: product?.id,
    slug: product?.slug || product?.product_url || "",
    real_slug: product?.slug || product?.product_url || "",
    name: product?.name || product?.product_name || "",
    brand:
      product?.brand ||
      product?.distributor ||
      product?.name ||
      product?.product_name ||
      "",
    price: product?.price ?? "",
    original_price: product?.price ?? "",
    discounted_price: product?.discounted_price ?? "",
    discountedPrice: product?.discounted_price ?? "",
    discount: Number(product?.discount ?? 0),
    discountPercent: Number(product?.discount ?? 0),
    image: product?.image || product?.image_url || "",
    image_url: product?.image_url || product?.image || "",
    matchPercentage: score,
    productType: "product",
    product_type: "product",
    is_marketplace: false,
  };
};

const CollectionSkeletonCard = () => {
  return (
    <article className="overflow-hidden rounded-[8px] bg-white">
      <div className="h-[560px] animate-pulse rounded-[7px] bg-[#E4E0DB] sm:h-[620px] md:h-[600px] lg:h-[640px] xl:h-[690px]" />
      <div className="flex items-center justify-between px-2 pb-1 pt-2">
        <div className="h-3 w-20 animate-pulse rounded bg-[#E4E0DB]" />
        <div className="h-3 w-10 animate-pulse rounded bg-[#E4E0DB]" />
      </div>
    </article>
  );
};

const CollectionProductsPage = () => {
  const authToken = localStorage.getItem("authToken");
  const isAuthenticated = Boolean(authToken);

  const [saveBoardProduct, setSaveBoardProduct] = useState(null);
  const [isSaveBoardModalOpen, setIsSaveBoardModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleCollectionProductClick = (product) => {
    navigate(
      `/product?productId=${product.id}&product_type=product${
        product.matchPercentage !== null &&
        product.matchPercentage !== undefined
          ? `&matchPercentage=${product.matchPercentage}`
          : ""
      }`,
      {
        state: {
          productId: product.id,
          productType: "product",
          product_type: "product",
          matchPercentage: product.matchPercentage,
        },
      },
    );
  };

  const signedInQuery = useGetProductsAllProductsQuery(
    {
      page: ALL_PRODUCTS_PAGE,
      pageSize: ALL_PRODUCTS_PAGE_SIZE,
    },
    {
      skip: !isAuthenticated,
      refetchOnMountOrArgChange: false,
    },
  );

  const publicQuery = usePublicProductsAllListQuery(
    {
      page: ALL_PRODUCTS_PAGE,
      pageSize: ALL_PRODUCTS_PAGE_SIZE,
    },
    {
      skip: isAuthenticated,
      refetchOnMountOrArgChange: false,
    },
  );

  const activeQuery = isAuthenticated ? signedInQuery : publicQuery;

  const products = useMemo(() => {
    if (!Array.isArray(activeQuery?.data?.results)) return [];

    return activeQuery.data.results.map(normalizeProductForCard);
  }, [activeQuery?.data]);

  const handleSaveBoard = (product) => {
    if (!isAuthenticated) {
      toast.error("Please sign in to save this product.");
      return;
    }

    if (!product) {
      toast.error("Product details are not available yet.");
      return;
    }

    const productSlug =
      product?.real_slug || product?.slug || product?.product_url || "";

    if (!productSlug) {
      toast.error("Product slug is not available yet.");
      return;
    }

    const visibleMatchPercentage = getVisibleCardMatchPercentage(product);

    setSaveBoardProduct({
      ...product,
      slug: productSlug,
      real_slug: productSlug,
      productType: "product",
      product_type: "product",
      is_marketplace: false,
      matchPercentage: visibleMatchPercentage,
      match_percentage: visibleMatchPercentage,
      image: product?.image || product?.image_url || "",
      image_url: product?.image_url || product?.image || "",
      name: product?.name || product?.product_name || "Product",
    });

    setIsSaveBoardModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#F6F4F1] text-black">
      <SEO
        title="All Products | Shop Eveness Fashion Collection"
        description="Browse all Eveness products, including dresses, fashion pieces, and curated styles. Explore product details, prices, sizes, and personalized fashion recommendations."
        path="/shop/products"
      />
      <NewNavbar bottomNav="none" />

      <main className="px-2 pb-10 pt-5 md:px-8 md:pt-8">
        <div className="mb-5 flex items-center justify-between gap-4 px-1 font-normal text-[11px] text-black md:mb-6 md:px-2 font-spaceMono">
          <div className="flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap">
            <Link to="/" className="hover:underline">
              Home
            </Link>
            <span>&gt;</span>
            <Link to="/shop" className="hover:underline">
              Collections
            </Link>
            <span>&gt;</span>
            <span>All products</span>
          </div>

          <Link to="/shop" className="shrink-0 hover:underline">
            Collections page
          </Link>
        </div>

        {activeQuery.isError && (
          <div className="mb-5 rounded-[4px] border border-red-200 bg-red-50 px-4 py-3 font-inter text-[13px] text-red-700">
            Failed to load products. Please try again.
          </div>
        )}

        <section className="grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {activeQuery.isLoading || activeQuery.isFetching
            ? Array.from({ length: 12 }).map((_, index) => (
                <CollectionSkeletonCard key={index} />
              ))
            : products.map((product) => (
                <ProductCard
                  key={product.id}
                  item={product}
                  imageHeightClass="h-[560px] sm:h-[620px] md:h-[600px] lg:h-[657px] xl:h-[690px]"
                  matchPercentage={product.matchPercentage}
                  compactActions
                  onSaveBoard={handleSaveBoard}
                  isSavingBoard={false}
                />
              ))}
        </section>

        {!activeQuery.isLoading &&
          !activeQuery.isFetching &&
          !products.length && (
            <div className="flex min-h-[300px] items-center justify-center text-center font-inter text-[14px] text-black/60">
              No products found.
            </div>
          )}
      </main>
      <SaveToBoardModal
        isOpen={isSaveBoardModalOpen}
        product={saveBoardProduct}
        onClose={() => {
          setIsSaveBoardModalOpen(false);
          setSaveBoardProduct(null);
        }}
        onRequireAuth={() => {
          setIsSaveBoardModalOpen(false);
          setSaveBoardProduct(null);
          toast.error("Please sign in to save this product.");
        }}
        onSaved={() => {
          setSaveBoardProduct(null);
        }}
      />
      <Footer />
    </div>
  );
};

export default CollectionProductsPage;
